/**
 * Per-field **Fixed | Expression** control (ADR-0009 §1, n8n-style).
 *
 * Every plain string config field can be set two ways, toggled per
 * field:
 *
 *   - **Fixed** — a literal value (plain `<Input>` / `<textarea>`),
 *     stored verbatim.
 *   - **Expression** — a string containing one or more `{{ … }}` JS
 *     segments, edited in the inline Monaco-JS editor with
 *     binding-aware IntelliSense (`$json`, `$item`, …).
 *
 * There is **no separate wire flag**: a value is an expression iff its
 * raw string contains `{{` (mirrors the backend's
 * `is_expression_template`). The UI mode is local state seeded from
 * that detection, so the two stay in lockstep across reloads while a
 * mid-edit user who momentarily deletes the braces isn't yanked out of
 * the editor.
 *
 * Runtime: the backend renders these templates via
 * `js_expr_layer.render_template` (type-preserving) — see
 * `docs/topics/engines/expression-language.md`.
 */
import * as React from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import type { ExprPreview } from "@/lib/pagePreview";
import { BINDING_DRAG_MIME } from "@/components/workflows/expressionPicker";

/** A value is an expression iff it carries at least one `{{` opener. */
export function isExpressionValue(value: unknown): boolean {
  return typeof value === "string" && value.includes("{{");
}

/** True when the drag carries a Schema-lens binding (the workflow drawer's
 *  data-picker drag). We key on the CUSTOM MIME, not text/plain — an
 *  arbitrary text or file drag must not be captured by the field. */
function isBindingDrag(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes(BINDING_DRAG_MIME);
}

/** The empty ƒx scaffold (`{{  }}`) — a drop REPLACES it instead of appending
 *  (the scaffold is a cursor target, not content worth keeping). */
const EMPTY_SCAFFOLD_RE = /^\{\{\s*\}\}$/;

/** Live-preview evaluator: maps a template to its rendered value/error, or
 *  null when there's nothing to preview. */
export type ExprPreviewFn = (template: string) => ExprPreview | null;

export interface ExpressionFieldProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  /** Render the Fixed editor as a growing textarea instead of a single
   *  line. Expression mode always uses the (taller) code editor. */
  multiline?: boolean;
  placeholder?: string;
  /** Ambient `.d.ts` for Monaco IntelliSense, derived from the node's
   *  sample input (see `expressionBindings.ts`). */
  jsAmbientTypes?: string;
  /** Optional live-preview evaluator: given the current template, return its
   *  rendered value (or error) to show under the editor, or `null` for nothing
   *  to preview. The page builder wires this to the sandboxed `pageExpr`
   *  interpreter against the page's live data (SYNCHRONOUS). The workflow
   *  editor evaluates server-side in QuickJS, so it wires {@link resolvePreview}
   *  (async) instead. */
  previewExpr?: ExprPreviewFn;
  /** Async live-preview evaluator (ADR-0068 F4) — the workflow editor's
   *  server round-trip. Debounced internally; the result renders in the same
   *  `= value` line as {@link previewExpr}. Ignored if `previewExpr` is set. */
  resolvePreview?: (template: string) => Promise<ExprPreview | null>;
  /** Register an "insert text at cursor" callback while this field's editor is
   *  focused (page builder wires the Data panel to it); called with `null` on
   *  unmount. Unset in the workflow editor. */
  registerInsert?: (insert: ((text: string) => void) | null) => void;
}

export function ExpressionField({
  id,
  value,
  onChange,
  multiline = false,
  placeholder,
  jsAmbientTypes,
  previewExpr,
  resolvePreview,
  registerInsert,
}: ExpressionFieldProps) {
  const detected = isExpressionValue(value);
  const [mode, setMode] = React.useState<"fixed" | "expr">(detected ? "expr" : "fixed");

  // Snap INTO expression mode when braces appear (e.g. the value was
  // loaded as an expression, or the user typed `{{`). We never snap
  // back to fixed automatically — losing the editor mid-edit because a
  // closing brace was briefly missing is hostile.
  React.useEffect(() => {
    if (detected) setMode("expr");
  }, [detected]);

  function toggle(next: "fixed" | "expr") {
    setMode(next);
    if (next === "expr" && !isExpressionValue(value)) {
      // Seed a brace scaffold so the field persists as an expression
      // and there's a cursor target. An existing literal is wrapped as
      // a JS string so its value is preserved (and type-preserved by
      // the single-expression rule).
      onChange(value ? `{{ ${JSON.stringify(value)} }}` : "{{  }}");
    }
  }

  const rows = Math.max(3, Math.min(10, Math.ceil((value ?? "").length / 60)));

  // Async live preview (ADR-0068 F4 — workflow editor). Debounced on `value`;
  // stale responses are dropped so a fast typist never sees an old result. The
  // sync `previewExpr` (page builder) wins when both are set.
  const [asyncPreview, setAsyncPreview] = React.useState<ExprPreview | null>(null);
  React.useEffect(() => {
    if (previewExpr || !resolvePreview || mode !== "expr") {
      setAsyncPreview(null);
      return;
    }
    let alive = true;
    const handle = window.setTimeout(() => {
      void resolvePreview(value ?? "").then((r) => {
        if (alive) setAsyncPreview(r);
      });
    }, 350);
    return () => {
      alive = false;
      window.clearTimeout(handle);
    };
  }, [value, resolvePreview, previewExpr, mode]);

  const preview: ExprPreview | null = previewExpr ? previewExpr(value ?? "") : asyncPreview;

  // Insert-at-cursor wiring: while the Monaco editor is focused, register an
  // insert callback so the Data panel can drop a `{{ … }}` binding at the
  // caret. We register on focus (last-focused field wins) and clear on unmount.
  type MonacoEditor = Parameters<
    NonNullable<React.ComponentProps<typeof CodeEditor>["onEditorMount"]>
  >[0];
  const editorRef = React.useRef<MonacoEditor | null>(null);
  React.useEffect(() => () => registerInsert?.(null), [registerInsert]);
  const onEditorMount = React.useCallback(
    (editor: MonacoEditor) => {
      editorRef.current = editor;
      if (!registerInsert) return;
      const insert = (text: string) => {
        const sel = editor.getSelection();
        if (sel)
          editor.executeEdits("insert-binding", [{ range: sel, text, forceMoveMarkers: true }]);
        editor.focus();
      };
      editor.onDidFocusEditorText(() => registerInsert(insert));
    },
    [registerInsert],
  );

  // ── Drag & drop from the Schema-lens picker (n8n model) ──────────────────
  // The drag payload rides `BINDING_DRAG_MIME` (+ text/plain); a drop lands
  // the `{{ … }}` binding IN THIS FIELD, wherever the field is:
  //   - ƒx mode → inserted at the DROP POSITION inside Monaco
  //     (`getTargetAtClientPoint`); the empty scaffold is replaced whole.
  //   - Fixed mode → the field flips to ƒx by construction (the appended
  //     braces make `isExpressionValue` true and the mode effect snaps).
  // Handlers are CAPTURE-phase on the wrapper so Monaco's own dnd machinery
  // never swallows the event first.
  const onBindingDragOver = React.useCallback((e: React.DragEvent) => {
    if (!isBindingDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const dropIntoValue = React.useCallback(
    (binding: string) => {
      const cur = value ?? "";
      if (!cur || EMPTY_SCAFFOLD_RE.test(cur.trim())) onChange(binding);
      else onChange(cur + binding);
    },
    [value, onChange],
  );

  const onEditorDrop = React.useCallback(
    (e: React.DragEvent) => {
      if (!isBindingDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      const binding =
        e.dataTransfer.getData(BINDING_DRAG_MIME) || e.dataTransfer.getData("text/plain");
      if (!binding) return;
      const editor = editorRef.current;
      const cur = value ?? "";
      // A whole-value replace (empty / bare scaffold) beats position math.
      if (!editor || !cur || EMPTY_SCAFFOLD_RE.test(cur.trim())) {
        dropIntoValue(binding);
        editor?.focus();
        return;
      }
      // Insert at the drop POINT — the reason drag beats click-to-insert.
      const target = editor.getTargetAtClientPoint?.(e.clientX, e.clientY);
      const pos = target?.position ?? editor.getPosition();
      if (pos) {
        editor.executeEdits("drop-binding", [
          {
            range: {
              startLineNumber: pos.lineNumber,
              startColumn: pos.column,
              endLineNumber: pos.lineNumber,
              endColumn: pos.column,
            },
            text: binding,
            forceMoveMarkers: true,
          },
        ]);
      } else {
        dropIntoValue(binding);
      }
      editor.focus();
    },
    [value, dropIntoValue],
  );

  const onFixedDrop = React.useCallback(
    (e: React.DragEvent) => {
      if (!isBindingDrag(e)) return;
      // Handle it ourselves — the browser's native text-drop would insert
      // WITHOUT flowing through the controlled onChange.
      e.preventDefault();
      const binding =
        e.dataTransfer.getData(BINDING_DRAG_MIME) || e.dataTransfer.getData("text/plain");
      if (binding) dropIntoValue(binding);
    },
    [dropIntoValue],
  );

  return (
    <div className="space-y-1">
      <div className="flex justify-end">
        <div
          role="group"
          aria-label="Field mode"
          className="inline-flex overflow-hidden rounded border border-border/60 text-[10px]"
        >
          <button
            type="button"
            aria-pressed={mode === "fixed"}
            onClick={() => toggle("fixed")}
            className={cn(
              "px-1.5 py-0.5 transition-colors",
              mode === "fixed"
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Fixed
          </button>
          <button
            type="button"
            aria-pressed={mode === "expr"}
            onClick={() => toggle("expr")}
            title="Expression (JavaScript)"
            className={cn(
              "border-l border-border/60 px-1.5 py-0.5 font-mono italic transition-colors",
              mode === "expr"
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            ƒx
          </button>
        </div>
      </div>

      {mode === "expr" ? (
        <>
          <div
            className={cn("w-full", multiline ? "h-40" : "h-24")}
            data-testid="expr-drop-target"
            onDragOverCapture={onBindingDragOver}
            onDropCapture={onEditorDrop}
          >
            <CodeEditor
              value={value ?? ""}
              onChange={onChange}
              language="javascript"
              height="100%"
              inline
              jsAmbientTypes={jsAmbientTypes}
              onEditorMount={onEditorMount}
            />
          </div>
          <ExprPreviewLine preview={preview} />
          <p className="text-[11px] text-muted-foreground">
            Wrap JS in <code className="font-mono">{"{{ }}"}</code>, e.g.{" "}
            <code className="font-mono">{"{{ $json.id }}"}</code>. A single{" "}
            <code className="font-mono">{"{{ expr }}"}</code> keeps its type; literal text or
            multiple segments → string.
          </p>
        </>
      ) : multiline ? (
        <textarea
          id={id}
          rows={rows}
          className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          onDragOver={onBindingDragOver}
          onDrop={onFixedDrop}
        />
      ) : (
        <Input
          id={id}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          onDragOver={onBindingDragOver}
          onDrop={onFixedDrop}
        />
      )}
    </div>
  );
}

/** Live result of the current expression: `= value` in the success tone, or the
 *  evaluation error. Renders nothing when there's nothing meaningful to preview
 *  (no evaluator / fixed literal / empty scaffold / result pending). */
function ExprPreviewLine({ preview }: { preview: ExprPreview | null }) {
  if (!preview) return null;
  if (preview.ok) {
    return (
      <p
        data-testid="expr-preview"
        className="truncate font-mono text-[11px] text-muted-foreground"
        title={preview.display}
      >
        <span className="text-success">= </span>
        <span>{preview.display}</span>
      </p>
    );
  }
  // n8n-style hint: the expression reads an upstream node's data that hasn't
  // been produced yet. Not a real error — a muted "run the previous nodes"
  // nudge, not the red eval-error tone.
  if (preview.needsUpstream) {
    return (
      <p
        data-testid="expr-preview-hint"
        className="truncate text-[11px] italic text-muted-foreground"
        title={preview.error}
      >
        {preview.error}
      </p>
    );
  }
  return (
    <p
      data-testid="expr-preview-error"
      className="truncate text-[11px] text-danger/80"
      title={preview.error}
    >
      {preview.error}
    </p>
  );
}
